import { useEffect, useState, useCallback } from 'react'
import { leadsAPI } from '@utils/api'
import AdminLayout from '@components/admin/AdminLayout'
import {
  Search, Filter, Phone, Mail, Clock, ChevronLeft, ChevronRight,
  Loader2, Plus, Trash2, Eye, X, PhoneCall, MessageSquare, AlertCircle
} from 'lucide-react'
import { format } from 'date-fns'
import toast from 'react-hot-toast'

const STATUSES = ['New', 'Contacted', 'Interested', 'Not Interested', 'Enrolled', 'Converted', 'Lost']
const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent']

const statusColors = {
  New: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  Contacted: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  Interested: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  Enrolled: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  Converted: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  Lost: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  'Not Interested': 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400',
}

const priorityDot = {
  Urgent: 'bg-red-500',
  High: 'bg-orange-500',
  Medium: 'bg-yellow-500',
  Low: 'bg-gray-400',
}

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</label>
    {children}
  </div>
)

const DetailModal = ({ lead, onClose, onUpdate }) => {
  const [status, setStatus] = useState(lead.status)
  const [priority, setPriority] = useState(lead.priority)
  const [note, setNote] = useState('')
  const [activityType, setActivityType] = useState('Call')
  const [activityDesc, setActivityDesc] = useState('')
  const [followUpDate, setFollowUpDate] = useState(lead.followUpDate ? lead.followUpDate.slice(0, 10) : '')
  const [saving, setSaving] = useState(false)

  const saveStatus = async () => {
    setSaving(true)
    try {
      await leadsAPI.update(lead._id, { status, priority, followUpDate: followUpDate || undefined })
      toast.success('Lead updated')
      onUpdate()
    } catch { toast.error('Update failed') }
    finally { setSaving(false) }
  }

  const addNote = async () => {
    if (!note.trim()) return
    setSaving(true)
    try {
      await leadsAPI.addNote(lead._id, { content: note })
      toast.success('Note added')
      setNote('')
      onUpdate()
    } catch { toast.error('Failed to add note') }
    finally { setSaving(false) }
  }

  const addActivity = async () => {
    if (!activityDesc.trim()) return
    setSaving(true)
    try {
      await leadsAPI.addActivity(lead._id, { type: activityType, description: activityDesc })
      toast.success('Activity logged')
      setActivityDesc('')
      onUpdate()
    } catch { toast.error('Failed to log activity') }
    finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl my-6">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">{lead.name}</h2>
            <p className="text-sm text-gray-500">{lead.email} · {lead.phone}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="City / Country">
              <span className="text-sm text-gray-800 dark:text-gray-200">{lead.city}, {lead.country}</span>
            </Field>
            <Field label="Experience">
              <span className="text-sm text-gray-800 dark:text-gray-200">{lead.experience}</span>
            </Field>
            <Field label="Course Interest">
              <span className="text-sm text-gray-800 dark:text-gray-200">{lead.courseInterest?.map(c => c.courseName).join(', ') || '—'}</span>
            </Field>
            <Field label="Source">
              <span className="text-sm text-gray-800 dark:text-gray-200">{lead.source}</span>
            </Field>
            <Field label="Budget">
              <span className="text-sm text-gray-800 dark:text-gray-200">{lead.budget || '—'}</span>
            </Field>
            <Field label="Timeline">
              <span className="text-sm text-gray-800 dark:text-gray-200">{lead.timeline || '—'}</span>
            </Field>
          </div>

          {lead.message && (
            <Field label="Message">
              <p className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-xl p-3">{lead.message}</p>
            </Field>
          )}

          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Status">
              <select value={status} onChange={e => setStatus(e.target.value)}
                className="w-full px-3 py-2 rounded-xl text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500">
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Priority">
              <select value={priority} onChange={e => setPriority(e.target.value)}
                className="w-full px-3 py-2 rounded-xl text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500">
                {PRIORITIES.map(p => <option key={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Follow-up Date">
              <input type="date" value={followUpDate} onChange={e => setFollowUpDate(e.target.value)}
                className="w-full px-3 py-2 rounded-xl text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </Field>
          </div>
          <button onClick={saveStatus} disabled={saving}
            className="px-5 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-2">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save Changes
          </button>

          <div className="border-t border-gray-100 dark:border-gray-800 pt-5">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Log Activity</h3>
            <div className="flex gap-2 mb-2">
              {['Call', 'Email', 'WhatsApp', 'Meeting'].map(t => (
                <button key={t} onClick={() => setActivityType(t)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${activityType === t ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-primary-400'}`}>
                  {t}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={activityDesc} onChange={e => setActivityDesc(e.target.value)}
                placeholder="Describe the activity…"
                className="flex-1 px-3 py-2 rounded-xl text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
              <button onClick={addActivity} disabled={saving || !activityDesc.trim()}
                className="px-4 py-2 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold disabled:opacity-60">
                Log
              </button>
            </div>
          </div>

          <div className="border-t border-gray-100 dark:border-gray-800 pt-5">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Add Note</h3>
            <div className="flex gap-2">
              <input value={note} onChange={e => setNote(e.target.value)}
                placeholder="Write a note…"
                className="flex-1 px-3 py-2 rounded-xl text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
              <button onClick={addNote} disabled={saving || !note.trim()}
                className="px-4 py-2 rounded-xl bg-gray-700 hover:bg-gray-800 text-white text-sm font-semibold disabled:opacity-60">
                Add
              </button>
            </div>
          </div>

          {(lead.activities?.length > 0 || lead.notes?.length > 0) && (
            <div className="border-t border-gray-100 dark:border-gray-800 pt-5 space-y-4">
              {lead.activities?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Activity History</h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                    {[...lead.activities].reverse().map((a, i) => (
                      <div key={i} className="flex items-start gap-3 text-sm bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 shrink-0">{a.type}</span>
                        <div className="flex-1">
                          <p className="text-gray-700 dark:text-gray-300">{a.description}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{a.date ? format(new Date(a.date), 'MMM d, yyyy h:mm a') : ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {lead.notes?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Notes</h3>
                  <div className="space-y-2 max-h-32 overflow-y-auto pr-1">
                    {[...lead.notes].reverse().map((n, i) => (
                      <div key={i} className="text-sm bg-yellow-50 dark:bg-yellow-900/10 rounded-xl p-3">
                        <p className="text-gray-700 dark:text-gray-300">{n.content}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{n.date ? format(new Date(n.date), 'MMM d, yyyy') : ''}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const AdminLeads = () => {
  const [leads, setLeads] = useState([])
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 })
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterPriority, setFilterPriority] = useState('')
  const [page, setPage] = useState(1)
  const [selectedLead, setSelectedLead] = useState(null)
  const [detailLead, setDetailLead] = useState(null)

  const fetchLeads = useCallback(async () => {
    setLoading(true)
    try {
      const res = await leadsAPI.getAll({
        page, limit: 15,
        search: search || undefined,
        status: filterStatus || undefined,
        priority: filterPriority || undefined,
        sortBy: 'createdAt', sortOrder: 'desc'
      })
      setLeads(res.data || [])
      setPagination(res.pagination || { page: 1, pages: 1, total: 0 })
    } catch { toast.error('Failed to load leads') }
    finally { setLoading(false) }
  }, [page, search, filterStatus, filterPriority])

  useEffect(() => { fetchLeads() }, [fetchLeads])

  const openDetail = async (lead) => {
    try {
      const res = await leadsAPI.getById(lead._id)
      setDetailLead(res.data)
    } catch { setDetailLead(lead) }
  }

  const deleteLead = async (id) => {
    if (!window.confirm('Delete this lead?')) return
    try {
      await leadsAPI.delete(id)
      toast.success('Lead deleted')
      fetchLeads()
    } catch { toast.error('Delete failed') }
  }

  return (
    <AdminLayout>
      {detailLead && (
        <DetailModal
          lead={detailLead}
          onClose={() => setDetailLead(null)}
          onUpdate={async () => {
            const res = await leadsAPI.getById(detailLead._id)
            setDetailLead(res.data)
            fetchLeads()
          }}
        />
      )}

      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 dark:text-white">Leads</h1>
            <p className="text-sm text-gray-500 mt-1">{pagination.total} total leads</p>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(1) }}
              placeholder="Search name, email, phone…"
              className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
          <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1) }}
            className="px-3 py-2.5 rounded-xl text-sm border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500">
            <option value="">All Status</option>
            {STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
          <select value={filterPriority} onChange={e => { setFilterPriority(e.target.value); setPage(1) }}
            className="px-3 py-2.5 rounded-xl text-sm border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500">
            <option value="">All Priority</option>
            {PRIORITIES.map(p => <option key={p}>{p}</option>)}
          </select>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-7 h-7 animate-spin text-primary-600" />
            </div>
          ) : leads.length === 0 ? (
            <div className="py-16 text-center">
              <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No leads found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Student</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Course</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Status</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Priority</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Follow-up</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Applied</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                  {leads.map((lead) => (
                    <tr key={lead._id} className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center shrink-0">
                            <span className="text-xs font-bold text-primary-700 dark:text-primary-400">{lead.name?.[0]?.toUpperCase()}</span>
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white">{lead.name}</p>
                            <p className="text-xs text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <p className="text-gray-700 dark:text-gray-300 max-w-[160px] truncate">
                          {lead.courseInterest?.map(c => c.courseName).join(', ') || '—'}
                        </p>
                        <p className="text-xs text-gray-400">{lead.preferredBatchMode}</p>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusColors[lead.status] || ''}`}>{lead.status}</span>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1.5">
                          <div className={`w-2 h-2 rounded-full ${priorityDot[lead.priority] || 'bg-gray-400'}`} />
                          <span className="text-xs text-gray-600 dark:text-gray-400">{lead.priority}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="text-xs text-gray-600 dark:text-gray-400">
                          {lead.followUpDate ? format(new Date(lead.followUpDate), 'MMM d, yyyy') : '—'}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="text-xs text-gray-500">{lead.createdAt ? format(new Date(lead.createdAt), 'MMM d, yyyy') : '—'}</span>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1">
                          <a href={`tel:${lead.phone}`} className="p-1.5 rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors">
                            <PhoneCall className="w-4 h-4" />
                          </a>
                          <button onClick={() => openDetail(lead)} className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button onClick={() => deleteLead(lead._id)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {pagination.pages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">Page {pagination.page} of {pagination.pages}</p>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}
                className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}

export default AdminLeads
